import { styled } from "@compiled/react";
import { useLocation } from "react-router-dom";

import { Inline, xcss } from "@atlaskit/primitives-next";
import Spinner from "@atlaskit/spinner";

import { FourOhFourDisplay } from "../../displays";
import { useRoutes } from "../../hooks";
import { UserAccessValidationState } from "../hooks/use-portal-authentication-and-access-control";

const PageContentContainer = styled.div({
    width: "100%",
    maxWidth: "1120px"
});

const LoadingContainerStyles = xcss({
    height: "80vh",
    width: "100%"
});

const LoadingPageView = () => {
    return (
        <PageContentContainer>
            <Inline
                alignInline="center"
                alignBlock="center"
                xcss={LoadingContainerStyles}
            >
                <Spinner
                    size="xlarge"
                    label="Loading"
                    testId="loading-spinner"
                />
            </Inline>
        </PageContentContainer>
    );
};

export const AccessControlledPageContent: React.FC<{
    userAccessState: UserAccessValidationState;
    children: React.ReactNode;
}> = ({
    userAccessState,
    children
}: {
    userAccessState: UserAccessValidationState;
    children: React.ReactNode;
}) => {
    const pathname = useLocation().pathname;
    const { paths } = useRoutes();

    // always allow home path to render children
    if (pathname === paths.home) {
        switch (userAccessState) {
            case UserAccessValidationState.LOADING_PORTAL_ELIGIBILITY:
            case UserAccessValidationState.LOADING_USER_PERMISSIONS:
                return <LoadingPageView />;
            case UserAccessValidationState.VALIDATION_SUCCESS:
            case UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE:
            case UserAccessValidationState.VALIDATION_FAIL_INELIGIBLE_FOR_PORTAL:
            default:
                return <PageContentContainer>{children}</PageContentContainer>;
        }
    }

    switch (userAccessState) {
        case UserAccessValidationState.LOADING_PORTAL_ELIGIBILITY:
        case UserAccessValidationState.LOADING_USER_PERMISSIONS:
            return <LoadingPageView />;
        case UserAccessValidationState.VALIDATION_SUCCESS:
            return <PageContentContainer>{children}</PageContentContainer>;
        case UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE:
        case UserAccessValidationState.VALIDATION_FAIL_INELIGIBLE_FOR_PORTAL:
        default:
            return <FourOhFourDisplay />;
    }
};
